import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { semanticCapabilities } from '../data/semanticKnowledge';
import { demoUrlFor } from '../data/demoCenter';
import { noetivraAvailabilityLabel } from '../data/siteContent';
import { fadeUp } from '../utils/motion';
import { PageHero } from '../components/layout/PageHero';
import { SkpLlmTeaser } from '../components/semantic/SkpLlmTeaser';
import { SkpWorkflow } from '../components/semantic/SkpWorkflow';
import { SkpResearch } from '../components/semantic/SkpResearch';
import { SkpCapabilityFoundation } from '../components/semantic/SkpCapabilityFoundation';
import { SkpControls } from '../components/semantic/SkpControls';
import { SkpFoundation } from '../components/semantic/SkpFoundation';
import { SkpCTA } from '../components/semantic/SkpCTA';
export function SemanticKnowledgePage() {
  return <>
      <PageHero eyebrow="Products" title="Semantic Knowledge Platform" lead="The knowledge infrastructure that connects and governs concepts, relationships, sources, and approved knowledge, with knowledge graphs, semantic search, and AI-assisted research." body={['It turns scattered research and content into connected, usable knowledge — defining concepts and relationships, linking evidence across sources, and pairing AI-assisted discovery with expert review.']} data-id="element-3493" data-mp="uyy9grvyplwg">
        <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8" data-id="element-3494" data-mp="1ho3gbp2omjlf">
          <a href="/contact" className="inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-3495" data-mp="1cqv2g0okh1pz">
            Contact Us
            <ArrowRight size={18} aria-hidden="true" data-id="element-3496" data-mp="20fe808g8f0ed" />
          </a>
          <a href="/semantic-knowledge#how-it-works" onClick={event => {
          event.preventDefault();
          document.getElementById('how-it-works')?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }} className="inline-flex items-center gap-2 text-navy font-semibold hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-3497" data-mp="1voebh4b0ykce">
            See how it works
          </a>
        </div>
      </PageHero>

      <section className="bg-surface-bg py-20 lg:py-28" data-id="element-3498" data-mp="3l36w9nwjzsm7">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-3499" data-mp="374z5jxq41njh">
          <motion.h2 {...fadeUp} className="lg:col-span-4 font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight leading-tight" data-id="element-3500" data-mp="3b8op28rxhexn">
            What the platform brings together
          </motion.h2>
          <motion.dl {...fadeUp} className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-x-12 border-t border-line" data-id="element-3501" data-mp="vfps8omlayt0">
            {semanticCapabilities.map(item => <div key={item.title} className="border-b border-line py-7" data-id="element-3502" data-mp="2kwjyikyf06on">
                <dt className="font-heading font-bold text-xl text-ink-dark mb-2" data-id="element-3503" data-mp="2fyidhuhfhxaj">{item.title}</dt>
                <dd className="text-ink-muted leading-relaxed" data-id="element-3504" data-mp="2umwf6233zfpi">{item.body}</dd>
              </div>)}
          </motion.dl>
        </div>
      </section>

      <SkpLlmTeaser data-id="element-3505" data-mp="2pv4z4lhkwsdm" />
      <SkpWorkflow data-id="element-3506" data-mp="2pulhz6zr680n" />
      <SkpResearch data-id="element-3507" data-mp="33nbe448zwtbh" />

      <section aria-labelledby="skp-noetivra" className="bg-surface-bg border-y border-line py-20 lg:py-28" data-id="element-3508" data-mp="33mt9aj89p6ry">
        <motion.div {...fadeUp} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-3509" data-mp="2ynyhirsbo7vp">
          <div className="lg:col-span-5" data-id="element-3510" data-mp="38n795dzptaew">
            <p className="text-sm font-medium text-teal mb-3" data-id="element-3511" data-mp="38ngz51krfhkl">Noetivra · {noetivraAvailabilityLabel}</p>
            <h2 id="skp-noetivra" className="font-heading font-bold text-3xl lg:text-4xl text-ink-dark tracking-tight leading-tight" data-id="element-3512" data-mp="1j8j3rwpjksqd">
              Explore connected knowledge through Noetivra
            </h2>
          </div>
          <div className="lg:col-span-7" data-id="element-3513" data-mp="aa9kklr20vcr">
            <p className="text-xl text-ink-dark leading-relaxed mb-6" data-id="element-3514" data-mp="a980xsvz30jf">
              The Noetivra Research demo shows how people can navigate approved
              relationships, inspect supporting passages, compare fictional
              studies and review proposals before they become knowledge. In
              production, those views would draw on knowledge maintained in SKP;
              that integration is in development.
            </p>
            <div className="flex flex-wrap gap-x-8 gap-y-3" data-id="element-3515" data-mp="3rrao2migbl8l">
              <a href={demoUrlFor('research')} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-navy font-semibold hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-3516" data-mp="3rpptiww502aq">
                Open Research Demo
                <ArrowUpRight size={16} aria-hidden="true" data-id="element-3517" data-mp="2wip903dttwlz" />
                <span className="sr-only" data-id="element-3518" data-mp="2wg2ybuvn30zj">(opens in a new tab)</span>
              </a>
              <a href="/noetivra" className="inline-flex items-center gap-2 text-navy font-semibold hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-3519" data-mp="2rh9jnz4m93xs">
                Explore Noetivra
                <ArrowRight size={16} aria-hidden="true" data-id="element-3520" data-mp="2dkmoo1w84nui" />
              </a>
            </div>
          </div>
        </motion.div>
      </section>

      <SkpCapabilityFoundation data-id="element-3521" data-mp="28qrwsb6gt2kc" />
      <SkpControls data-id="element-3522" data-mp="23t9wtaafdxoh" />
      <SkpFoundation data-id="element-3523" data-mp="23uten5bqcgsd" />
      <SkpCTA data-id="element-3524" data-mp="23ub6babsmc43" />
    </>;
}